import DynamicForm, { FormValuesOutput } from '@policygenius/mortar-dynamic-forms';
import { useCreateDataPointValues, useGetDataPointValues } from 'api/hooks/useDataPointValues';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { completedNavigatorStep } from 'analytics/navigator';
import { StepName } from 'analytics/navigator/constants';
import DynamicFormWrapper from 'components/DynamicFormWrapper';
import GlobalLoader from 'components/GlobalLoader';
import NavigatorContent from 'components/NavigatorContent';
import { underWritingDIPDS } from 'containers/utils/useNavigateUnderwritingStep';
import { CONTINUE_ONLINE_CRIMINAL_HISTORY_PATH } from 'routes/constants';
import useSelector from 'store/useSelector';

import schema from './schema';
import { FormValues } from './types';
import { buildInitialValues, buildSubmissionValues } from './utils';

export const MARIJUANA_URNS: Array<keyof FormValues> = [
  'urn:pg:data-point:insured-risk-condition-marijuana-history-type-medicinal',
  'urn:pg:data-point:insured-risk-condition-marijuana-recreational-usage-form-other-details',
  'urn:pg:data-point:insured-risk-condition-marijuana-medicinal-prescription-reason',
  'urn:pg:data-point:insured-risk-condition-marijuana-average-use-per-month',
  'urn:pg:data-point:insured-risk-condition-marijuana-date-last-used',
  'urn:pg:data-point:insured-risk-condition-marijuana-additional-details',
];

export const RECREATIONAL_MARIJUANA_URNS = [
  'urn:pg:data-point:insured-risk-condition-marijuana-recreational-usage-form-inhales',
  'urn:pg:data-point:insured-risk-condition-marijuana-recreational-usage-form-smoke',
  'urn:pg:data-point:insured-risk-condition-marijuana-recreational-usage-form-oral',
  'urn:pg:data-point:insured-risk-condition-marijuana-recreational-usage-form-tinctures',
  'urn:pg:data-point:insured-risk-condition-marijuana-recreational-usage-form-other',
];

const MarijuanaDetails: React.FC = () => {
  const activeUserUuid = useSelector((app) => app.activeUserUuid);
  const navigate = useNavigate();

  const { data, loading } = useGetDataPointValues({
    params: {
      urns: [...underWritingDIPDS, ...MARIJUANA_URNS, ...RECREATIONAL_MARIJUANA_URNS],
    },
  });

  const { createDataPointValues } = useCreateDataPointValues({
    onCompleted: () => {
      completedNavigatorStep(StepName.MARIJUANA_DETAILS, activeUserUuid);

      navigate(`../${CONTINUE_ONLINE_CRIMINAL_HISTORY_PATH}`);
    },
  });

  const onSubmit = async (values: FormValuesOutput<FormValues>) => {
    await createDataPointValues(buildSubmissionValues(values));
  };

  if (loading) {
    return <GlobalLoader />;
  }

  const initialValues = buildInitialValues(data?.dataPointValues);

  return (
    <NavigatorContent>
      <DynamicFormWrapper>
        <DynamicForm<FormValues>
          name="marijuana-details"
          schema={schema}
          onSubmit={(values) => onSubmit(values)}
          initialValues={initialValues}
        />
      </DynamicFormWrapper>
    </NavigatorContent>
  );
};

export default MarijuanaDetails;
